import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  usePurchaseCosmetic as usePurchaseCosmeticMutation,
  newIdempotencyKey,
  resolveErrorMessage,
} from '@kidshome/api-contract';
import { useToast } from '@/shared/ui';
import { tDynamic } from '@/shared/lib/i18n';
import { invalidateHeroSurfaces } from '@/entities/hero';

/**
 * `purchase-cosmetic` — the kid "Buy" flow (ADR-0029 coin sink). Wraps the purchase mutation with:
 *   - a STABLE idempotency key per item-buy intent (minted once per item, reused on retry/double-tap;
 *     offline-safe via the PWA outbox so a replay or a second tap never enqueues a second entry under a
 *     different key — the route is `@Idempotent`),
 *   - a kind success toast ("New {{item}}!") — or a calm "already yours" note when the server reports
 *     the business no-op (`purchased: false`, e.g. a stale catalog on a second device),
 *   - invalidation on settle of the catalog + equipped views, the child's balance, AND the play read
 *     surfaces (pets/backgrounds/heroes) — a skin spends ✨ from the same ledger those reads carry their
 *     own copy of, so they share the ShopPage's ✨ balance/affordability and must reconcile together, and
 *   - kind error surfacing (the new cosmetics codes: locked/over-level, not-owned, insufficient funds)
 *     translated client-side — no red-alarm copy.
 *
 * Concurrency: in-flight items are tracked as a SET (each card's busy state is independent), and the
 * toast/invalidate/clear callbacks live on the MUTATION (not per `mutate` call) — TanStack Query drops
 * a call's own callbacks when `mutate` fires again before the first settles, which would strand the
 * first card busy forever and skip its invalidation.
 *
 * `childId` scopes the balance invalidation to the signed-in child (cosmetics are self-scoped).
 */
export function usePurchaseCosmetic({
  childId,
}: {
  /** The signed-in child's id, for balance-cache invalidation; null disables that one invalidation. */
  childId: string | null;
}): {
  /** Fire a purchase for `itemId`, showing a success toast naming `itemName` on settle. */
  buy: (itemId: string, itemName: string) => void;
  /** The item ids currently in flight (so each card can show its own pending state). */
  pendingItemIds: ReadonlySet<string>;
} {
  const { t } = useTranslation(['family', 'errors']);
  const toast = useToast();
  const qc = useQueryClient();
  const [pendingItemIds, setPendingItemIds] = useState<ReadonlySet<string>>(new Set());
  // Display names for in-flight items (the toast needs the name; mutation vars only carry the id).
  const namesRef = useRef(new Map<string, string>());
  // One STABLE idempotency key per item — reused across re-taps/retries of the SAME buy so an offline
  // replay or a double-tap can't enqueue a second outbox entry under a different key (cosmetics are
  // owned-once and the route is @Idempotent, but a stable key closes the double-enqueue window directly).
  const keysRef = useRef(new Map<string, string>());

  const purchase = usePurchaseCosmeticMutation({
    onSuccess: (data, vars) => {
      const item = namesRef.current.get(vars.itemId) ?? '';
      // Offline: the PWA outbox queues the write and resolves `data` as undefined (no server body
      // yet). Skip the toast in that case — the mutation is queued, not a confirmed purchase.
      if (!data) return;
      // `purchased: false` is the server's kind already-owned no-op (no coins were spent) —
      // confirm ownership calmly instead of celebrating a purchase that didn't happen.
      if (data.purchased) {
        toast.success(t('family:shop.toast.purchased', { item }));
      } else {
        toast.success(t('family:shop.toast.alreadyOwned', { item }));
      }
    },
    onError: (error) => {
      const { key, params } = resolveErrorMessage(error);
      toast.error(tDynamic(t, key, params));
    },
    onSettled: (_data, _error, vars) => {
      namesRef.current.delete(vars.itemId);
      // Clear only THIS item's busy flag — other purchases may still be in flight.
      setPendingItemIds((prev) => {
        const next = new Set(prev);
        next.delete(vars.itemId);
        return next;
      });
      // A cosmetic buy SPENDS ✨ from the same ledger balance the play read surfaces carry their own copy
      // of (pets/backgrounds/heroes each return `stars`), so reconcile ALL of them — not just the cosmetics
      // caches — or the Skins/Pets/Scenes tabs on the shared ShopPage disagree on the ✨ balance and a kid
      // can tap Collect on a pet they can no longer afford. `invalidateHeroSurfaces` already invalidates the
      // cosmetics catalog + equipped looks and the family balance (scoped to childId), so it covers it all.
      invalidateHeroSurfaces(qc, childId);
    },
  });

  const buy = useCallback(
    (itemId: string, itemName: string): void => {
      namesRef.current.set(itemId, itemName);
      const existing = keysRef.current.get(itemId);
      const idempotencyKey = existing ?? newIdempotencyKey();
      if (existing === undefined) keysRef.current.set(itemId, idempotencyKey);
      setPendingItemIds((prev) => new Set(prev).add(itemId));
      purchase.mutate({ itemId, idempotencyKey });
    },
    [purchase],
  );

  return { buy, pendingItemIds };
}
